import _flatten from 'lodash/flatten';
import { MessageDescriptor } from 'react-intl';
import { v4 as uuid } from 'uuid';
import _omit from 'lodash/omit';
import QuestionnaireItemUpdater from './QuestionnaireMapper/QuestionnaireItemUpdater';
import CategoryItem from '../Components/SurveyBuilder/PageEditor/NewLibraryQuestionButton/CategoryItem';

type UserSelectedCoding = Omit<fhir.Coding, 'userSelected'> & {
  userSelected: true;
};

export type ContentServiceQuestion = {
  project: string;
  displayName: string;
  type: 'survey question';
  fields: {
    categoryId: {
      value: string;
    };
    question: {
      value: {
        libraryQuestionId: string;
        title: MessageDescriptor;
        item: LibrarySurveyItem[];
      };
    };
  };
  slug: string;
  public: true;
  version: 1;
};

export type LibrarySurveyItem = Omit<
  fhir.QuestionnaireItem,
  'text' | 'item' | 'linkId' | 'code'
> & {
  text: MessageDescriptor;
  item?: LibrarySurveyItem[];
  code?: [UserSelectedCoding];
};

export type QuestionLibraryQuestion = {
  libraryQuestionId: string;
  item?: LibrarySurveyItem[];
  title: MessageDescriptor;
  id: string;
};

export type CategoryLabel = {
  id: string;
  defaultMessage: string;
};

export type CategoryItem = CategoryNode | QuestionLibraryQuestion;

export function isCategoryNode(
  categoryItem: CategoryItem,
): categoryItem is CategoryNode {
  return !!(categoryItem as CategoryNode).categoryId;
}

export function toCategoryLabel(categoryId: string): CategoryLabel {
  // e.g. would convert "physical-characteristics" to "Physical Characteristics"
  const defaultMessage = categoryId.replace(/(^\w|-\w)/g, text =>
    text.replace(/-/, ' ').toUpperCase(),
  );
  const id = categoryId + '.label';
  return {
    id,
    defaultMessage,
  };
}

export function displayTextToSlug(categoryText: string): string {
  // e.g. would convert "Physical Characteristics" to "physical-characteristics"
  return (categoryText || '').toLocaleLowerCase().split(' ').join('-');
}

type FormatMessage = (message: MessageDescriptor) => string;

export type CategoryNode = {
  categoryId: string;
  items: CategoryItem[];
};

export function libraryItemToQuestionnaireItem(
  librarySurveyItem: LibrarySurveyItem,
  formatMessage: FormatMessage,
): fhir.QuestionnaireItem {
  const linkId = uuid();
  const text = formatMessage(librarySurveyItem.text);

  if (!librarySurveyItem.item?.length) {
    const item: fhir.QuestionnaireItem = {
      ..._omit(librarySurveyItem, 'item'),
      linkId,
      text,
    };

    const updater = new QuestionnaireItemUpdater(item);
    if (item.type !== 'group') {
      return updater.setGeneratedCode().questionnaireItem;
    } else {
      return updater.questionnaireItem;
    }
  } else {
    const subItems = librarySurveyItem.item.map(_ =>
      libraryItemToQuestionnaireItem(_, formatMessage),
    );

    return {
      ...librarySurveyItem,
      linkId,
      text,
      item: subItems,
    } as fhir.QuestionnaireItem;
  }
}

export function questionnaireItemToLibraryItem(
  questionnaireItem: fhir.QuestionnaireItem,
): LibrarySurveyItem {
  const { code, linkId, item, text, ...toPass } = questionnaireItem;

  const librarySurveyItem: LibrarySurveyItem = {
    ...toPass,
    text: {
      id: uuid(),
      defaultMessage: text,
    },
  };

  if (item?.length) {
    librarySurveyItem.item = item.map(questionnaireItemToLibraryItem);
  }

  const specifiedUserCode = (code || []).find(
    _ => !!_?.userSelected,
  ) as UserSelectedCoding;
  if (specifiedUserCode) {
    librarySurveyItem.code = [specifiedUserCode];
  }

  return librarySurveyItem;
}

export function libraryItemsIdToQuestionnaireItems(
  targetLibraryItem: QuestionLibraryQuestion,
  formatMessage: FormatMessage,
): fhir.QuestionnaireItem[] {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return targetLibraryItem.item.map(_ =>
    libraryItemToQuestionnaireItem(_, formatMessage),
  );
}

type QuestionParentCategories = {
  categories: string[];
  question: QuestionLibraryQuestion;
};

function buildParentCategories(
  categories: CategoryNode[],
): QuestionParentCategories[] {
  const recursiveBuild = (
    categoryNode: CategoryNode,
    parentCategories: string[],
  ): QuestionParentCategories[] => {
    return _flatten(
      categoryNode.items.map(_ => {
        if (isCategoryNode(_)) {
          return recursiveBuild(_, [...parentCategories, _.categoryId]);
        } else {
          return [
            {
              categories: parentCategories,
              question: _,
            },
          ];
        }
      }),
    );
  };

  return _flatten(categories.map(_ => recursiveBuild(_, [_.categoryId])));
}

export function doesCategoryHaveAnyQuestion(
  category: CategoryNode,
  questions: string[],
): boolean {
  return questions
    ? category.items.some(item =>
        questions.includes((item as QuestionLibraryQuestion).libraryQuestionId),
      )
    : false;
}

export type QuestionLibrarySearchData = {
  searchText: string;
  questionId: string;
};

export function buildQuestionLibrarySearchData(
  categories: CategoryNode[],
  formatMessage: FormatMessage,
): QuestionLibrarySearchData[] {
  const mappedCategories = buildParentCategories(categories).map(_ => {
    const descriptors = [
      ..._.categories.map(_ => toCategoryLabel(_)),
      _.question.title,
    ];

    return {
      searchText: descriptors.map(_ => formatMessage(_)).join(' | '),
      questionId: _.question.libraryQuestionId,
    };
  });
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return mappedCategories.length ? mappedCategories : null;
}
